import { AUDIO_DECODE_FAILURE_MESSAGE } from "@/constants/audio";
import { AudioInputState, UserNoDates } from "@/types/types";
import { getAudioLengthClient } from "@/utils/getAudioLength/getAudioLengthClient";
import { isValidLengthSubscription } from "@/utils/subscriptionLevels/time/isValidLengthSubscription";

export const handleFileUpload = async ({
  files,
  user,
  setAuthModalOpen,
  checkSubscription,
  setInvalidLengthSubscriptionModalOpen,
  audioLengthMinLimitMinutes,
  audioLengthMaxLimitMinutes,
  setInputFiles,
  currentAudioLengthS,
  setError,
  maxMultiAudioLengthMinutes,
}: {
  files: File[];
  user: UserNoDates | null;
  setAuthModalOpen: () => void;
  checkSubscription: boolean;
  setInvalidLengthSubscriptionModalOpen: (value: boolean) => void;
  audioLengthMinLimitMinutes: number | null;
  audioLengthMaxLimitMinutes: number;
  setInputFiles: AudioInputState["setInputFile"][];
  currentAudioLengthS: number;
  setError: (value: string | null) => void;
  maxMultiAudioLengthMinutes: number | null;
}) => {
  setError(null);

  if (user === null) {
    setAuthModalOpen();
    return;
  }

  // The decode is the first thing that can reject a file the accept filter let through (a renamed text file, a truncated upload). getAudioLengthClient already captures the failure with per-file context, so this is the one place the user hears about it.
  let audioLengthFiles: number[];
  try {
    audioLengthFiles = await Promise.all(
      files.map((file) => getAudioLengthClient(file))
    );
  } catch (error) {
    console.error(error);
    setError(AUDIO_DECODE_FAILURE_MESSAGE);
    return;
  }

  const totalAudioLengthS =
    audioLengthFiles.reduce((acc, curr) => acc + curr, 0) + currentAudioLengthS;

  if (
    maxMultiAudioLengthMinutes !== null &&
    totalAudioLengthS > maxMultiAudioLengthMinutes * 60
  ) {
    setError(`Maximum length is ${maxMultiAudioLengthMinutes} minutes.`);
    return;
  }

  if (checkSubscription) {
    const validSubscription = isValidLengthSubscription(
      user.conversionTimeLeftSeconds,
      totalAudioLengthS
    );

    if (!validSubscription) {
      setInvalidLengthSubscriptionModalOpen(true);
      return;
    }
  }

  for (const audioLengthFile of audioLengthFiles) {
    const validMin =
      audioLengthMinLimitMinutes === null ||
      audioLengthFile >= audioLengthMinLimitMinutes * 60;
    const validMax = audioLengthFile <= audioLengthMaxLimitMinutes * 60;

    if (!validMin) {
      setError(`Minimum length is ${audioLengthMinLimitMinutes} minutes.`);
      return;
    }

    if (!validMax) {
      setError(`Maximum length is ${audioLengthMaxLimitMinutes} minutes.`);
      return;
    }
  }

  for (let i = 0; i < Math.min(setInputFiles.length, files.length); i++) {
    const isFirstFile = i === 0 && currentAudioLengthS === 0;
    setInputFiles[i]({ file: files[i], isFirstFile });
  }
};
